import React, { useState, useEffect } from 'react';
import { Heart, Target, Eye, CheckCircle, Users, Shield, TrendingUp, Award, HandHeart, Globe, Zap, Utensils, Package, Activity } from 'lucide-react';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../../../assets/css/general/About.css';
import NavigationBar from '../../../components/user/general/NavigationBar';
import Footer from '../footer/Footer';

const About = () => {
  const [counters, setCounters] = useState({
    donors: 0,
    ngos: 0,
    donations: 0,
    beneficiaries: 0
  });

  const [isVisible, setIsVisible] = useState({});

  useEffect(() => {
    const interval = setInterval(() => {
      setCounters(prev => ({
        donors: prev.donors < 5420 ? prev.donors + 50 : 5420,
        ngos: prev.ngos < 150 ? prev.ngos + 2 : 150,
        donations: prev.donations < 12500 ? prev.donations + 100 : 12500,
        beneficiaries: prev.beneficiaries < 25000 ? prev.beneficiaries + 200 : 25000
      }));
    }, 30);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll('.animate-on-scroll');
      sections.forEach((section) => {
        const rect = section.getBoundingClientRect();
        const isInView = rect.top < window.innerHeight * 0.8;
        if (isInView && !section.classList.contains('animated')) {
          section.classList.add('animated');
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const features = [
    { 
      icon: <Users className="feature-icon" />, 
      title: 'Donor Module', 
      desc: 'Easy registration and secure donation posting with complete tracking system',
      color: '#1a5f5a'
    },
    { 
      icon: <Shield className="feature-icon" />, 
      title: 'NGO Module', 
      desc: 'Manage campaigns, track donations, and connect with verified donors',
      color: '#2d7a6e'
    },
    { 
      icon: <Heart className="feature-icon" />, 
      title: 'Receiver Module', 
      desc: 'Request help easily and get support quickly from verified sources',
      color: '#1a5f5a'
    },
    { 
      icon: <TrendingUp className="feature-icon" />, 
      title: 'Real-time Tracking', 
      desc: 'Monitor every step of your donation journey with live updates',
      color: '#2d7a6e'
    },
    { 
      icon: <CheckCircle className="feature-icon" />, 
      title: 'Verification System', 
      desc: 'Ensure authenticity of all requests through our robust verification',
      color: '#1a5f5a'
    },
    { 
      icon: <Award className="feature-icon" />, 
      title: 'Reward System', 
      desc: 'Recognition and rewards for top donors and active participants',
      color: '#f4a300'
    }
  ];

  const donationTypes = [
    { icon: <Utensils size={40} />, title: 'Food Donation', desc: 'Making a difference in lives every day', progress: 75, color: '#1a5f5a' },
    { icon: <Package size={40} />, title: 'Medicine Donation', desc: 'Healthcare support for those in need', progress: 60, color: '#2d7a6e' },
    { icon: <Activity size={40} />, title: 'Blood Donation', desc: 'Save lives with your contribution', progress: 85, color: '#e74c3c' }
  ];

  const stats = [
    { icon: <Users size={40} />, count: counters.donors, label: 'Active Donors' },
    { icon: <HandHeart size={40} />, count: counters.ngos, label: 'Partner NGOs' },
    { icon: <Heart size={40} />, count: counters.donations, label: 'Donations Made' },
    { icon: <Globe size={40} />, count: counters.beneficiaries, label: 'Lives Impacted' }
  ];

  const objectives = [
    'Easy registration for all users',
    'Quick donation posting',
    'Smart matching system',
    'Real-time status updates',
    'Secure communication',
    'Track complete donation journey',
    'Reward system for top donors',
    'AI-based recommendations'
  ];

  const values = [
    { icon: <Heart />, title: 'Compassion', desc: 'We believe in the power of empathy and kindness' },
    { icon: <Shield />, title: 'Transparency', desc: 'Every donation is tracked and accounted for' },
    { icon: <Globe />, title: 'Impact', desc: 'Creating real, measurable change in communities' },
    { icon: <Zap />, title: 'Efficiency', desc: 'Fast, secure, and reliable donation processing' }
  ];

  return (
    <div className="about-page">
      <NavigationBar/>
      {/* Hero Section */}
      <section className="hero-section-charity">
        <div className="hero-pattern"></div>
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <div className="hero-content-charity fade-in-up">
                <div className="hero-badge-charity">
                  <Utensils size={18} /> FOOD DONATION
                </div>
                <h1 className="hero-title-charity">
                  Smart Donation Management System
                </h1>
                <h2 className="hero-subtitle-charity">Feed The Hungry</h2>
                <p className="hero-tagline">Share Your Blessings</p>
                <p className="hero-description-charity">
                  "Donate Easily. Help Quickly. Track Transparently."
                </p>
                <p className="hero-text-charity">
                  Every meal you donate brings hope to a family in need. Join us in fighting hunger.
                </p>
                <div className="hero-buttons-charity">
                  <button className="btn-donate-now">Donate Now</button>
                  <button className="btn-learn-more">Learn More</button>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="donation-cards-wrapper">
                {donationTypes.map((type, idx) => (
                  <div key={idx} className={`floating-donation-card card-${idx + 1}`}>
                    <div className="card-icon-circle" style={{backgroundColor: `${type.color}20`, color: type.color}}>
                      {type.icon}
                    </div>
                    <div className="card-content">
                      <h4>{type.title}</h4>
                      <p>{type.desc}</p>
                      <div className="progress-bar-container">
                        <div className="progress-bar-fill" style={{width: `${type.progress}%`, backgroundColor: type.color}}></div>
                      </div>
                      <span className="progress-text">{type.progress}% Goal Achieved</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section-charity animate-on-scroll">
        <div className="container">
          <div className="row">
            {stats.map((stat, idx) => (
              <div key={idx} className="col-lg-3 col-md-6 mb-4">
                <div className="stat-card-charity" style={{animationDelay: `${idx * 0.1}s`}}>
                  <div className="stat-icon-charity">{stat.icon}</div>
                  <div className="stat-number-charity">{stat.count.toLocaleString()}+</div>
                  <div className="stat-label-charity">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="about-content-section-charity animate-on-scroll">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6 mb-4">
              <div className="about-image-container">
                <div className="about-circle-shape"></div>
                <div className="about-icon-wrapper pulse-animation">
                  <Heart size={120} />
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <span className="section-tag">About Us</span>
              <h2 className="section-title-charity">Empowering Generosity Through Technology</h2>
              <p className="section-description">
                Smart Donation Management System ek online platform hai jo donors, NGOs aur receivers 
                ko aik jagah connect karta hai. Hamara mission hai donation process ko fast, easy 
                aur transparent banana.
              </p>
              <p className="section-description">
                Users food, medicine, blood aur funds donate kar sakte hain ya help request kar sakte hain. 
                Our intelligent matching system ensures that every donation reaches the right person at the 
                right time, creating maximum impact.
              </p>
              <div className="check-list-charity">
                {['Real-time donation tracking', 'Verified NGOs and receivers', 'Secure payment processing', 'Transparent reporting system'].map((item, idx) => (
                  <div key={idx} className="check-item-charity" style={{animationDelay: `${idx * 0.1}s`}}>
                    <CheckCircle size={20} /> {item}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Solution */}
      <section className="problem-solution-section-charity animate-on-scroll">
        <div className="container">
          <div className="text-center mb-5">
            <span className="section-tag">Challenges We Address</span>
            <h2 className="section-title-charity">Making Charity More Effective</h2>
          </div>
          <div className="row">
            <div className="col-lg-6 mb-4">
              <div className="problem-card-charity slide-in-left">
                <div className="card-header-charity problem">
                  <div className="icon-badge problem">❌</div>
                  <h3>Traditional Challenges</h3>
                </div>
                <ul className="challenge-list">
                  <li>Donations proper logon tak nahi pohanchti</li>
                  <li>Fake requests aur fraud cases</li>
                  <li>Poor communication between stakeholders</li>
                  <li>No proper tracking system</li>
                  <li>Time-consuming manual processes</li>
                </ul>
              </div>
            </div>
            <div className="col-lg-6 mb-4">
              <div className="solution-card-charity slide-in-right">
                <div className="card-header-charity solution">
                  <div className="icon-badge solution">✓</div>
                  <h3>Our Solutions</h3>
                </div>
                <ul className="solution-list-charity">
                  <li>Direct connection between donors & receivers</li>
                  <li>AI-powered verification system</li>
                  <li>Built-in chat and notification system</li>
                  <li>Complete donation tracking dashboard</li>
                  <li>Automated, efficient workflows</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Vision */}
      <section className="mission-vision-section-charity animate-on-scroll">
        <div className="container">
          <div className="row">
            <div className="col-lg-6 mb-4">
              <div className="mv-card-charity mission zoom-in">
                <div className="mv-icon-charity">
                  <Target size={50} />
                </div>
                <h3 className="mv-title-charity">Our Mission</h3>
                <p className="mv-text-charity">
                  Our mission is to create a smart, fast and reliable donation ecosystem where 
                  every donation reaches the right person at the right time. We strive to eliminate 
                  barriers in charitable giving and ensure transparency at every step.
                </p>
              </div>
            </div>
            <div className="col-lg-6 mb-4">
              <div className="mv-card-charity vision zoom-in" style={{animationDelay: '0.2s'}}>
                <div className="mv-icon-charity">
                  <Eye size={50} />
                </div>
                <h3 className="mv-title-charity">Our Vision</h3>
                <p className="mv-text-charity">
                  To build a transparent digital donation network across the country with verified 
                  NGOs, responsible donors, and genuine receivers. We envision a future where technology 
                  empowers compassion and creates lasting social impact.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="values-section-charity animate-on-scroll">
        <div className="container">
          <div className="text-center mb-5">
            <span className="section-tag">Our Values</span>
            <h2 className="section-title-charity">What Drives Us Forward</h2>
          </div>
          <div className="row">
            {values.map((value, idx) => (
              <div key={idx} className="col-lg-3 col-md-6 mb-4">
                <div className="value-card-charity bounce-in" style={{animationDelay: `${idx * 0.1}s`}}>
                  <div className="value-icon-charity">{value.icon}</div>
                  <h4 className="value-title-charity">{value.title}</h4>
                  <p className="value-desc-charity">{value.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Objectives */}
      <section className="objectives-section-charity animate-on-scroll">
        <div className="container">
          <div className="text-center mb-5">
            <span className="section-tag">Project Objectives</span>
            <h2 className="section-title-charity">Our Goals & Targets</h2>
          </div>
          <div className="row">
            {objectives.map((obj, idx) => (
              <div key={idx} className="col-lg-6 mb-3">
                <div className="objective-card-charity slide-in-item" style={{animationDelay: `${idx * 0.05}s`}}>
                  <CheckCircle className="objective-icon" size={20} />
                  <span>{obj}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="features-section-charity animate-on-scroll">
        <div className="container">
          <div className="text-center mb-5">
            <span className="section-tag">Key Features</span>
            <h2 className="section-title-charity">Powerful Tools for Maximum Impact</h2>
            <p className="section-subtitle-charity">
              Everything you need to make charity work seamlessly
            </p>
          </div>
          <div className="row">
            {features.map((feature, idx) => (
              <div key={idx} className="col-lg-4 col-md-6 mb-4">
                <div className="feature-card-charity scale-in" style={{animationDelay: `${idx * 0.1}s`}}>
                  <div className="feature-icon-box-charity" style={{backgroundColor: `${feature.color}15`, color: feature.color}}>
                    {feature.icon}
                  </div>
                  <h4 className="feature-title-charity">{feature.title}</h4>
                  <p className="feature-desc-charity">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section-charity">
        <div className="cta-pattern"></div>
        <div className="container">
          <div className="cta-box-charity pulse-glow">
            <div className="row align-items-center">
              <div className="col-lg-8">
                <h2 className="cta-title-charity">Ready to Make a Difference?</h2>
                <p className="cta-text-charity">
                  Join thousands of donors and NGOs creating positive change every day
                </p>
              </div>
              <div className="col-lg-4 text-lg-end">
                <button className="btn-cta-charity">
                  Get Started <span className="arrow-icon">→</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
       <Footer/>
    </div>
  );
  
};

export default About;